/**
 * One pass from a `chat.messages` DTO row to everything the chat UI needs to
 * render it — `normalizeWaMessage()`.
 *
 * Why this exists:
 *   • The bubble used to re-parse `payloadJson`/`dataJson` once per candidate
 *     card (eight independent `JSON.parse` calls per message, per render).
 *   • Rendering decisions were spread across a ternary ladder, a `renderMessage`
 *     if-chain and eight `incoming &&` / `!incoming &&` guards, so several real
 *     message types (an inbound interactive reply, an outbound location, an
 *     `address`/`product`/`product_list`/`button` row) reached the screen as a
 *     bare emoji — or, when `message` happened to be null, as nothing at all.
 *
 * The rule this module enforces: **every message yields a non-empty `body` or
 * a structured payload**. A bubble can never render blank, and an unknown or
 * future Meta type degrades to its humanised registry label rather than
 * disappearing. React components downstream only read this view — they never
 * touch raw Meta JSON.
 *
 * Pure and React-free on purpose, so the whole type matrix is unit-testable.
 */
import {
  formatWaLabel,
  isWaUnsupported,
  splitWaMessageType,
  unsupportedKindFromPayload,
  waMediaKind,
  waTypeLabel,
  waUnsupportedHint,
  waUnsupportedLabel,
  type WaMediaKind,
} from '@leadsync/shared-types';
import {
  buildPreviewFromSentTemplate,
  type SentTemplateSnapshot,
} from './template-preview-builder';
import { parseInteractivePayload, type InteractivePayload } from './interactive-payload';
import type { PreviewTemplate } from '../components/TemplatePreview';

/** Which renderer in the registry draws this message's body. Media types
 *  collapse onto `media` (the player is chosen by `media.kind`); `sticker` is
 *  separate because it renders without bubble chrome. */
export type WaMessageKind =
  | 'text'
  | 'media'
  | 'sticker'
  | 'location'
  | 'address'
  | 'contacts'
  | 'order'
  | 'product'
  | 'interactive'
  | 'button'
  | 'template'
  | 'flow'
  | 'flow_response'
  | 'reaction'
  | 'system'
  | 'unsupported';

/** Mirrors the API's `mediaStatus`. `pending` means the mirror worker has not
 *  fetched the bytes yet and the bubble should show a placeholder that will
 *  upgrade itself — as opposed to `unavailable`, which never will. */
export type WaMediaStatus = 'available' | 'pending' | 'unavailable';

export interface WaMediaView {
  kind: WaMediaKind;
  url: string | null;
  status: WaMediaStatus;
  /** Original Meta filename — documents only. */
  fileName: string | null;
  mimeType: string | null;
  /** "🎤 Voice message" / "🎵 Audio" — audio has no visual of its own. */
  label: string;
}

export interface WaLocationView {
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
}

export interface WaContactView {
  name: string;
  phones: string[];
}

export interface WaOrderItemView {
  id: string;
  quantity: number;
  price: number;
  currency: string;
}

export interface WaProductView {
  /** One entry for `product`, several for `product_list`. */
  retailerIds: string[];
  catalogId: string | null;
  sectionTitles: string[];
}

export interface WaFlowView {
  flowCta: string;
  headerText: string | null;
  footerText: string | null;
  formName: string | null;
}

export interface WaUnsupportedView {
  label: string;
  hint: string | null;
  /** Meta's raw errors[] reason — diagnostics, shown only in a tooltip. */
  reason: string | null;
}

/** What the UI is allowed to offer for this message. Derived once here so no
 *  component has to enumerate message types to decide (`capabilities.reactable`
 *  instead of `type === 'text' || type === 'image' || …`). */
export interface WaCapabilities {
  /** Meta accepts a reply/reaction only against a real wamid. */
  replyable: boolean;
  reactable: boolean;
  /** Meta's edit endpoint covers outbound text within a 15-minute window. */
  editable: boolean;
  downloadable: boolean;
  /** Renders as a centred pill (system/call), not a chat bubble. */
  pill: boolean;
  /** Has a structured card below the bubble. */
  card: boolean;
}

export interface WaMessageView {
  kind: WaMessageKind;
  /** Meta's finer-grained discriminator where one exists:
   *  `button_reply` | `list_reply` | `nfm_reply` for interactive,
   *  the `unsupported:<kind>` suffix for unsupported, `product_list` for
   *  a multi-product share. Null when the kind is already precise. */
  subtype: string | null;
  /** Always non-empty. The guaranteed-renderable line for this message. */
  fallbackLabel: string;
  /** Text to draw inside the bubble. Non-null unless media/a card carries the
   *  whole message — so a bubble is never both empty and card-less. */
  body: string | null;
  /** Caption under a media bubble (already stripped of the document filename). */
  caption: string | null;
  media: WaMediaView | null;
  location: WaLocationView | null;
  contacts: WaContactView[] | null;
  order: WaOrderItemView[] | null;
  product: WaProductView | null;
  template: PreviewTemplate | null;
  flow: WaFlowView | null;
  flowResponse: Array<{ key: string; value: string }> | null;
  interactive: InteractivePayload | null;
  /** The option the customer actually picked, for an inbound interactive. */
  interactiveSelectionId: string | null;
  reactionEmoji: string | null;
  unsupported: WaUnsupportedView | null;
  capabilities: WaCapabilities;
}

/** The subset of the `chat.messages` DTO the normalizer reads. */
export interface WaMessageInput {
  isIncoming: boolean;
  message: string | null;
  messageType: string | null;
  wamid?: string | null;
  dataJson?: string | null;
  payloadJson?: string | null;
  mediaUrl?: string | null;
  mimeType?: string | null;
  mediaStatus?: WaMediaStatus | null;
  interactiveSelectionId?: string | null;
  reactionEmoji?: string | null;
  errorMessage?: string | null;
  errorCode?: number | null;
}

function parse<T>(json: string | null | undefined): T | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

function trimmed(v: string | null | undefined): string | null {
  const t = v?.trim();
  return t ? t : null;
}

/** Loosely-typed mirror of the inbound Meta payload fields the UI reads. It
 *  crosses the free-form `payloadJson` boundary, not a typed API response. */
interface WaPayload {
  document?: { filename?: string };
  location?: { latitude?: number; longitude?: number; name?: string; address?: string };
  contacts?: Array<{ name?: { formatted_name?: string }; phones?: Array<{ phone?: string }> }>;
  order?: {
    catalog_id?: string;
    product_items?: Array<{
      product_retailer_id?: string;
      quantity?: number;
      item_price?: number;
      currency?: string;
    }>;
  };
  product?: { catalog_id?: string; product_retailer_id?: string };
  product_list?: {
    catalog_id?: string;
    sections?: Array<{
      title?: string;
      product_items?: Array<{ product_retailer_id?: string }>;
    }>;
  };
  interactive?: {
    type?: string;
    button_reply?: { id?: string; title?: string };
    list_reply?: { id?: string; title?: string; description?: string };
    nfm_reply?: { response_json?: string; name?: string };
  };
  address?: { values?: Record<string, string> };
}

/* ───────────────────────── per-payload readers ───────────────────────── */

/** Original filename Meta sends for inbound documents (only documents carry
 *  one). Kept out of `message` parsing — that column also holds the caption. */
function documentFileName(payload: WaPayload | null): string | null {
  return trimmed(payload?.document?.filename);
}

/**
 * Caption to show *below* a media bubble. For documents the stored `message` is
 * `"<filename> — <caption>"` (filename first, so it makes the best sidebar
 * preview) — but the filename is already on the attachment chip, so strip it
 * here rather than printing it twice.
 */
function mediaCaption(message: string | null, fileName: string | null): string | null {
  const text = trimmed(message);
  if (!text) return null;
  if (!fileName) return text;
  if (text === fileName) return null;
  return text.startsWith(`${fileName} — `) ? text.slice(fileName.length + 3) : text;
}

function readLocation(p: WaPayload | null): WaLocationView | null {
  const loc = p?.location;
  if (!loc || loc.latitude == null || loc.longitude == null) return null;
  return {
    latitude: loc.latitude,
    longitude: loc.longitude,
    ...(loc.name ? { name: loc.name } : {}),
    ...(loc.address ? { address: loc.address } : {}),
  };
}

function readContacts(p: WaPayload | null): WaContactView[] | null {
  const out = (p?.contacts ?? []).map((c) => ({
    name: c.name?.formatted_name ?? 'Contact',
    phones: (c.phones ?? []).map((ph) => ph.phone ?? '').filter(Boolean),
  }));
  return out.length ? out : null;
}

function readOrder(p: WaPayload | null): WaOrderItemView[] | null {
  const out = (p?.order?.product_items ?? []).map((it) => ({
    id: it.product_retailer_id ?? '',
    quantity: it.quantity ?? 0,
    price: it.item_price ?? 0,
    currency: it.currency ?? '',
  }));
  return out.length ? out : null;
}

function readProduct(p: WaPayload | null): WaProductView | null {
  if (p?.product) {
    return {
      retailerIds: p.product.product_retailer_id ? [p.product.product_retailer_id] : [],
      catalogId: p.product.catalog_id ?? null,
      sectionTitles: [],
    };
  }
  const list = p?.product_list;
  if (!list) return null;
  const sections = list.sections ?? [];
  return {
    retailerIds: sections.flatMap((s) =>
      (s.product_items ?? []).map((i) => i.product_retailer_id ?? '').filter(Boolean),
    ),
    catalogId: list.catalog_id ?? null,
    sectionTitles: sections.map((s) => trimmed(s.title) ?? '').filter(Boolean),
  };
}

/** Pull a flow-submission's answers off an inbound nfm_reply's payloadJson. */
function readFlowResponse(p: WaPayload | null): Array<{ key: string; value: string }> | null {
  const responseJson = p?.interactive?.nfm_reply?.response_json;
  if (!responseJson) return null;
  const answers = parse<Record<string, unknown>>(responseJson);
  if (!answers) return null;
  const out = Object.entries(answers)
    .filter(([k, v]) => k !== 'flow_token' && v !== null && v !== undefined && String(v) !== '')
    .map(([key, v]) => ({ key, value: typeof v === 'string' ? v : JSON.stringify(v) }));
  return out.length ? out : null;
}

/** Structured payload for an outbound WhatsApp Flow ("form") send so the bubble
 *  can render it the way WhatsApp does — header / body / footer plus a
 *  call-to-action — instead of just the form name. Covers both send paths:
 *  `chat-command-service.sendFlow` and the forms feature (`flow_name`). */
function readFlow(dataJson: string | null | undefined): WaFlowView {
  const p = parse<{
    flowCta?: string;
    headerText?: string | null;
    footerText?: string | null;
    flow_name?: string | null;
  }>(dataJson);
  return {
    flowCta: trimmed(p?.flowCta) ?? 'Open form',
    headerText: trimmed(p?.headerText),
    footerText: trimmed(p?.footerText),
    formName: trimmed(p?.flow_name),
  };
}

/** Inbound interactive replies arrive on `payloadJson` (the option the customer
 *  tapped); outbound interactive sends are written to `dataJson` by the cascade
 *  engine (the options offered). Read both so the card renders either way —
 *  previously only the outbound shape was handled, so an inbound list reply
 *  showed as one line of text. */
function readInboundInteractive(p: WaPayload | null): InteractivePayload | null {
  const i = p?.interactive;
  if (i?.list_reply) {
    return {
      interactiveType: 'list',
      listButtonText: 'Selected',
      rows: [
        {
          id: String(i.list_reply.id ?? 'row_0'),
          title: String(i.list_reply.title ?? ''),
          ...(i.list_reply.description ? { description: i.list_reply.description } : {}),
        },
      ],
    };
  }
  if (i?.button_reply) {
    return {
      interactiveType: 'button',
      buttons: [
        { id: String(i.button_reply.id ?? 'btn_0'), title: String(i.button_reply.title ?? '') },
      ],
    };
  }
  return null;
}

function readAddress(p: WaPayload | null): string | null {
  const values = p?.address?.values ?? {};
  return trimmed(Object.values(values).filter(Boolean).join(', '));
}

/* ─────────────────────────── kind resolution ─────────────────────────── */

/** Stored `messageType` → the registry key that draws it. Everything Meta has
 *  not taught us about lands on `unsupported`, which renders a soft note. */
function resolveKind(messageType: string | null): WaMessageKind {
  if (!messageType) return 'text';
  if (isWaUnsupported(messageType)) return 'unsupported';
  if (waMediaKind(messageType)) return messageType === 'sticker' ? 'sticker' : 'media';
  switch (messageType) {
    case 'text':
    case 'request_welcome':
      return 'text';
    case 'system':
    case 'call':
      return 'system';
    case 'location':
      return 'location';
    case 'address':
      return 'address';
    case 'contacts':
      return 'contacts';
    case 'order':
      return 'order';
    case 'product':
    case 'product_list':
      return 'product';
    case 'interactive':
      return 'interactive';
    case 'button':
      return 'button';
    case 'template':
      return 'template';
    case 'flow':
      return 'flow';
    case 'flow_response':
      return 'flow_response';
    case 'reaction':
      return 'reaction';
    default:
      // A type Meta added after this build shipped. It still gets a humanised
      // label from the shared registry rather than vanishing.
      return 'unsupported';
  }
}

/** The line to show when a message has no better content of its own. Never
 *  empty — this is what makes a blank bubble impossible. */
function resolveFallbackLabel(messageType: string | null, errorCode: number | null): string {
  const label = waTypeLabel(messageType ?? undefined, errorCode ?? undefined);
  if (label) return formatWaLabel(label);
  // `waTypeLabel` returns null for the types that carry their own readable text
  // (text / template). Those normally have a `message`; this is the last resort
  // for the rows where they do not.
  return messageType && messageType !== 'text' && messageType !== 'template'
    ? formatWaLabel({ icon: '📎', label: 'Message' })
    : 'Message';
}

function resolveSubtype(
  messageType: string | null,
  payload: WaPayload | null,
  unsupportedKind: string | null,
): string | null {
  if (unsupportedKind) return unsupportedKind;
  if (messageType === 'product_list') return 'product_list';
  if (messageType === 'interactive' || messageType === 'flow_response') {
    return payload?.interactive?.type ?? null;
  }
  if (messageType === 'call' || messageType === 'request_welcome') return messageType;
  return null;
}

/* ─────────────────────────────── entry point ─────────────────────────── */

export function normalizeWaMessage(m: WaMessageInput): WaMessageView {
  const messageType = m.messageType;
  const kind = resolveKind(messageType);
  const payload = parse<WaPayload>(m.payloadJson);
  const errorCode = m.errorCode ?? null;
  const fallbackLabel = resolveFallbackLabel(messageType, errorCode);
  const text = trimmed(m.message);

  const mediaKind = waMediaKind(messageType);
  const fileName = documentFileName(payload);
  const media: WaMediaView | null = mediaKind
    ? {
        kind: mediaKind,
        url: m.mediaUrl ?? null,
        // Older API builds don't send `mediaStatus`; infer it from the URL so
        // the bubble degrades to the pre-existing behaviour rather than lying.
        status: m.mediaStatus ?? (m.mediaUrl ? 'available' : 'unavailable'),
        fileName,
        mimeType: m.mimeType ?? null,
        label: fallbackLabel,
      }
    : null;

  const location = kind === 'location' ? readLocation(payload) : null;
  const contacts = kind === 'contacts' ? readContacts(payload) : null;
  const order = kind === 'order' ? readOrder(payload) : null;
  const product = kind === 'product' ? readProduct(payload) : null;
  const flow = kind === 'flow' ? readFlow(m.dataJson) : null;
  const flowResponse = kind === 'flow_response' ? readFlowResponse(payload) : null;
  const template =
    messageType === 'template'
      ? buildPreviewFromSentTemplate(
          parse<{ sentTemplate?: SentTemplateSnapshot }>(m.dataJson)?.sentTemplate,
        )
      : null;
  const interactive =
    kind === 'interactive'
      ? (parseInteractivePayload({ messageType, dataJson: m.dataJson }) ??
        readInboundInteractive(payload))
      : null;

  // Only a real `type: "unsupported"` webhook gets the unsupported treatment.
  // A type Meta simply added after this build shipped also lands on the
  // `unsupported` renderer, but it must keep its own humanised label
  // ("📎 Holographic note") rather than being flattened to the generic
  // "Unsupported message" — it is unknown to us, not undeliverable by Meta.
  const isMetaUnsupported = isWaUnsupported(messageType);
  const unsupportedKind = isMetaUnsupported
    ? (splitWaMessageType(messageType).unsupportedKind ??
      unsupportedKindFromPayload(m.payloadJson) ??
      null)
    : null;
  const unsupported: WaUnsupportedView | null = isMetaUnsupported
    ? {
        label: formatWaLabel(waUnsupportedLabel(unsupportedKind, errorCode)),
        hint: waUnsupportedHint(unsupportedKind, errorCode),
        reason: trimmed(m.errorMessage),
      }
    : null;

  const addressSummary = kind === 'address' ? (readAddress(payload) ?? text) : null;
  const caption = media ? mediaCaption(m.message, fileName) : null;
  const reactionEmoji = kind === 'reaction' ? (trimmed(m.reactionEmoji) ?? text ?? '👍') : null;

  // The bubble text. Media, stickers, reactions, rich templates and soft notes
  // own their whole presentation; every other kind falls back to the registry
  // label so it can never render empty.
  const hasOwnVisual =
    media !== null ||
    kind === 'sticker' ||
    kind === 'reaction' ||
    (kind === 'template' && template !== null) ||
    unsupported !== null;
  const body = hasOwnVisual
    ? null
    : kind === 'address'
      ? (addressSummary ?? fallbackLabel)
      : (text ?? fallbackLabel);

  const card =
    location !== null ||
    contacts !== null ||
    order !== null ||
    product !== null ||
    flow !== null ||
    flowResponse !== null ||
    interactive !== null ||
    (template !== null &&
      ((template.buttons?.length ?? 0) > 0 || (template.carouselCards?.length ?? 0) > 0));

  return {
    kind,
    subtype: resolveSubtype(messageType, payload, unsupportedKind),
    fallbackLabel,
    body,
    caption,
    media,
    location,
    contacts,
    order,
    product,
    template,
    flow,
    flowResponse,
    interactive,
    interactiveSelectionId: m.interactiveSelectionId ?? null,
    reactionEmoji,
    unsupported,
    capabilities: {
      // Meta keys replies and reactions off the wamid — without one there is
      // nothing to address, whatever the type.
      replyable: !!m.wamid && kind !== 'system' && kind !== 'reaction',
      reactable: !!m.wamid && kind !== 'system' && kind !== 'reaction',
      editable: !m.isIncoming && messageType === 'text',
      downloadable: media !== null && media.url !== null,
      pill: kind === 'system',
      card,
    },
  };
}

/** One-line plain-text summary of a message — for the composer's reply-preview
 *  bar and anywhere else a bubble is quoted. Never empty. */
export function waPreviewText(m: WaMessageInput): string {
  const view = normalizeWaMessage(m);
  return view.body ?? view.caption ?? view.reactionEmoji ?? view.fallbackLabel;
}
